import React from "react";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { Form } from "../../../forms/Form";
import { FormSubmit } from "../../../forms/FormSubmit";
import {
    IProductUUID,
    IReviewsProductID,
    IReviewsProductVariantID,
    IWebPageURL,
    isoFormUUID,
} from "../../../models/nominals";
import {
    IReviewsProductVariant,
    IWriteReviewTemplate,
} from "../../../models/reviews";
import { useAppDispatch, useAppSelector } from "../../reducers";
import {
    SideNames,
    SubmitReviewStatus,
    WriteReviewFormMode,
} from "../constants";
import { defaultReviewTemplate, defaultState } from "../defaults";
import { MultiProductWriteReviewFormGlobalInputSection } from "../elements/MultiProductWriteReviewFormGlobalInputSection";
import { MultiProductWriteReviewFormSection } from "../elements/MultiProductWriteReviewFormSection";
import { WriteReviewFormDisclaimers } from "../elements/WriteReviewFormDisclaimers";
import { WriteReviewMultiThankYou } from "../elements/WriteReviewMultiThankYou";
import {
    IReviewFormProductTemplatePair,
    IWriteReviewFormState,
} from "../models";
import {
    registerNewWriteReviewForm,
    reviewFormVariantChange,
} from "../reducers";
import { getAllFormSubmittedForParentUUID } from "../selectors";
import { loadProduct, loadReviewTemplate, submitReview } from "../thunks";

const getWriteReviewTemplateForForm = (
    templates: readonly IReviewFormProductTemplatePair[],
    formState: IWriteReviewFormState,
): IWriteReviewTemplate => {
    const formTemplate = templates.find(
        ({ productID }) => productID === formState.selectedProductID,
    );
    return formTemplate ? formTemplate.template : defaultReviewTemplate;
};

const scrollToThankYou = (): void => {
    const document = getDocument();
    if (!document) {
        return;
    }
    const thankYouSelectDiv = document.querySelector(".review-thank-you");
    thankYouSelectDiv?.scrollIntoView({ behavior: "smooth" });
};

const listProducts = async (
    productID: IReviewsProductID,
    parentUUID: IProductUUID,
    variantIDs: IReviewsProductVariantID[],
    dispatch: ReturnType<typeof useAppDispatch>,
): Promise<void> => {
    const productResult = await dispatch(loadProduct(productID));
    if (!loadProduct.fulfilled.match(productResult)) {
        return;
    }
    const product = productResult.payload;

    await Promise.all(
        variantIDs.map(async (variantID) => {
            const uuid = isoFormUUID.wrap(`${parentUUID}__${variantID}`);
            dispatch(
                registerNewWriteReviewForm({
                    formUUID: uuid,
                    mode: WriteReviewFormMode.FORM_OPEN,
                }),
            );
            // Load and dispatch the review template
            await dispatch(
                loadReviewTemplate({
                    formUUID: uuid,
                    productID: productID,
                }),
            );
            // Find variant info from review product, update selection of form
            const reviewVariantInfo = product.variants.find(
                (v: IReviewsProductVariant) => v.id === variantID,
            );
            if (reviewVariantInfo) {
                dispatch(
                    reviewFormVariantChange({
                        formUUID: uuid,
                        selectedVariant: reviewVariantInfo.slug,
                    }),
                );
            }
        }),
    );
};

export const HeterogenousProductReviewForm = (props: {
    parentUUID: IProductUUID;
    productID: IReviewsProductID;
    variantIDs: IReviewsProductVariantID[];
    returnURL?: IWebPageURL;
}): React.ReactNode => {
    const dispatch = useAppDispatch();

    const state = useAppSelector(
        (rootState) => rootState.reviews || defaultState,
    );
    const allSubmitted = useAppSelector((rootState) =>
        getAllFormSubmittedForParentUUID(rootState.reviews, props),
    );
    const loadedInitialProducts = state.data.loadedInitialProducts;
    const writeReviewTemplates = state.data.writeReviewTemplates;
    const products = state.data.products;
    const forms = state.ui.writeReviewForms;
    const prevAllSubmittedRef = React.useRef(allSubmitted);

    React.useEffect(() => {
        void listProducts(
            props.productID,
            props.parentUUID,
            props.variantIDs,
            dispatch,
        );
    }, []);

    React.useEffect(() => {
        const prevAllSubmitted = prevAllSubmittedRef.current;
        if (!prevAllSubmitted && allSubmitted) {
            scrollToThankYou();
        }
        prevAllSubmittedRef.current = allSubmitted;
    }, [allSubmitted]);

    const onSubmit = async (
        e: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        e.preventDefault();
        const isMultiReview = true;
        const submitStatuses = [];
        for (const [uuidstr, formState] of Object.entries(forms)) {
            const uuid = isoFormUUID.wrap(uuidstr);
            const formTemplate = getWriteReviewTemplateForForm(
                writeReviewTemplates,
                formState,
            );
            try {
                const result = await dispatch(
                    submitReview({
                        formUUID: uuid,
                        template: formTemplate,
                        form: formState,
                        isMultiReview,
                    }),
                ).unwrap();
                submitStatuses.push(result.status);
            } catch {
                submitStatuses.push(SubmitReviewStatus.FAIL_API);
            }
        }
        if (submitStatuses.every((s) => s === SubmitReviewStatus.SUCCESS)) {
            scrollToThankYou();
        }
    };

    if (!loadedInitialProducts) {
        return null;
    }

    return (
        <div className="write-review-form multi-product-write-review-form">
            {!allSubmitted && (
                <>
                    <p className="write-review-form__sub-title">
                        (
                        <span className="write-review-form__sub-title--red">
                            *
                        </span>
                        ){" "}
                        <span className="write-review-form__sub-title--italic">
                            {t`indicates a required field`}
                        </span>
                    </p>
                    <Form onSubmit={onSubmit} noValidate={true}>
                        {products.map((product) => {
                            return props.variantIDs.map((variantID, idx) => {
                                const formUUID = isoFormUUID.wrap(
                                    `${props.parentUUID}__${variantID}`,
                                );
                                const side = SideNames[idx];
                                return (
                                    <MultiProductWriteReviewFormSection
                                        key={String(formUUID)}
                                        formUUID={formUUID}
                                        product={product}
                                        isMultiReview={true}
                                        side={side}
                                    />
                                );
                            });
                        })}
                        ;
                        <MultiProductWriteReviewFormGlobalInputSection />
                        <WriteReviewFormDisclaimers />
                        <FormSubmit
                            className="button button--rock-blue write-review-form__cta"
                            type="submit"
                            value={t`Submit your review`}
                        />
                    </Form>
                </>
            )}
            <WriteReviewMultiThankYou parentUUID={props.parentUUID} />
        </div>
    );
};
